import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { firstName, useAuth } from '../lib/auth';
import { getMyDashboard, listMyCertificates } from '../lib/api';
import { friendlyError } from '../lib/supabase';
import type { Certificate, Course, EnrollmentProgress, Lesson } from '../lib/types';
import { CourseCard, courseImage } from '../components/CourseCard';
import { Badge, ButtonLink, Callout, Panel, ProgressBar, SectionTitle, Spinner, StatTile } from '../components/ui';

interface Data { progress: EnrollmentProgress[]; courses: Course[]; lessons: Pick<Lesson, 'id' | 'title' | 'course_id'>[]; certificates: Certificate[] }

export function Dashboard() {
  const { user, profile } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let live = true;
    Promise.all([getMyDashboard(user.id), listMyCertificates(user.id)])
      .then(([d, certificates]) => { if (live) setData({ ...d, certificates }); })
      .catch((e) => { if (live) setError(friendlyError(e)); });
    return () => { live = false; };
  }, [user]);

  if (error) return <div className="container page"><Callout variant="error">{error}</Callout></div>;
  if (!data) return <Spinner label="Loading your dashboard…" />;

  const courseById = new Map(data.courses.map((c) => [c.id, c]));
  const active = [...data.progress].sort((a, b) => (b.last_access_at ?? b.enrolled_at).localeCompare(a.last_access_at ?? a.enrolled_at));
  const current = active.find((p) => p.status === 'active') ?? null;
  const currentCourse = current ? courseById.get(current.course_id) : undefined;
  const lastLesson = current?.last_lesson_id ? data.lessons.find((l) => l.id === current.last_lesson_id) : undefined;
  const lessonsDone = data.progress.reduce((s, p) => s + p.completed_lessons, 0);
  const lessonsTotal = data.progress.reduce((s, p) => s + p.required_lessons, 0);
  const pdhEarned = data.certificates.reduce((s, c) => s + Number(c.pdh), 0);

  return (
    <>
      <section className="title-band">
        <div className="container title-band-inner">
          <div>
            <SectionTitle overline="Student dashboard" subtitle={
              current && currentCourse ? `You're ${Math.round(current.percent_complete)}% through ${currentCourse.title}.`
                : data.progress.length ? 'All caught up. Nice work.' : 'Browse the catalog to start your first course.'}>
              Welcome back, {firstName(profile, user)}
            </SectionTitle>
          </div>
          {current && currentCourse
            ? <ButtonLink to={`/learn/${currentCourse.slug}`} size="lg" iconAfter="chevronRight">Continue learning</ButtonLink>
            : <ButtonLink to="/courses" size="lg" variant="enroll">Browse courses</ButtonLink>}
        </div>
      </section>

      <div className="container page">
        <section aria-label="Learning summary" className="stats-grid">
          <StatTile label="Enrolled courses" value={data.progress.length} />
          <StatTile label="Lessons completed" value={lessonsTotal ? `${lessonsDone} of ${lessonsTotal}` : '0'} />
          <StatTile label="Professional development hours" value={`${pdhEarned} PDH`} hint="Awarded with each certificate" />
          <StatTile label="Certificates" value={data.certificates.length} />
        </section>

        {current && currentCourse && (
          <section className="continue-card" aria-labelledby="continue-h">
            <img src={courseImage(currentCourse)} alt="" />
            <div className="body">
              <span className="overline">Continue</span>
              <h2 id="continue-h">{lastLesson?.title ?? currentCourse.title}</h2>
              <p className="small muted" style={{ margin: 0 }}>{currentCourse.title}</p>
              <ProgressBar value={current.percent_complete} label="Course progress" />
              <div className="row"><ButtonLink to={`/learn/${currentCourse.slug}${lastLesson ? `/${lastLesson.id}` : ''}`} icon="play">Resume lesson</ButtonLink></div>
            </div>
          </section>
        )}

        <div className="two-col">
          <div className="main">
            <SectionTitle level={2}>My courses</SectionTitle>
            {data.progress.length === 0 ? (
              <div className="empty">You're not enrolled in a course yet. <Link to="/courses">Browse the catalog</Link>.</div>
            ) : (
              <div className="cards-grid">
                {active.map((p) => {
                  const c = courseById.get(p.course_id);
                  return c ? <CourseCard key={p.enrollment_id} course={c} progress={Number(p.percent_complete)} /> : null;
                })}
              </div>
            )}
          </div>
          <aside className="side">
            <Panel title="Certificates">
              {data.certificates.length ? (
                <ul className="list-box" style={{ border: 0 }}>
                  {data.certificates.map((c) => (
                    <li key={c.id} style={{ padding: '8px 0' }}>
                      <Badge tone="success">{Number(c.pdh)} PDH</Badge>
                      <span style={{ flex: '1 1 160px' }}>{c.course_title}</span>
                      <Link to={`/verify/${c.public_id}`} className="small">Verify</Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0, fontSize: 15 }}>Finish every required lesson and assessment in a course to earn its certificate.</p>
              )}
            </Panel>
            <Callout variant="note">Your progress saves automatically and follows you to any device.</Callout>
          </aside>
        </div>
      </div>
    </>
  );
}
